import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import { Eye, Plus } from 'lucide-react'
import Loader from '../../../common/components/Loader'
import { Admins } from '../../../common/models'
import { IColumn, Table } from '../../../core/components/Table'
import { sanitizeDate } from '../../../core/constants/times'
import { useTableParams } from '../../../core/hooks/table-params'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import { getGeneralSettingsAdmins } from '../../../core/services/general-settings'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { CreateAdminModal } from './CreateNewAdminModal'
import { Icon } from '@/common/components/Icon'

export const AdminsPage: React.FC = (): JSX.Element => {
  const { page, setPage, size, setSize, totalElements, setTotalElemenets } =
    useTableParams()

  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })

  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [admins, setAdmins] = useState<Admins[]>([])
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)

  const navigate = useNavigate()
  const { editionId, festivalName } = useParams()

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const resp = await getGeneralSettingsAdmins({ page, size })
      if (resp.status === 200) {
        setAdmins(resp.data)
        // Note: API doesn't return pagination info, so we'll keep existing totalElements
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [page, size])

  useEffect(() => {
    getData()
  }, [getData])

  const pageChange = async (newPage: number) => {
    setPage(newPage)
  }

  const handleSelectAll = () => {
    if (!hasEditPermission) {
      return
    }
    setIsCheckAll(!isCheckAll)
    setIsCheck(admins.map((adm) => adm.id))
    if (isCheckAll) {
      setIsCheck([])
    }
  }

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) {
      return
    }
    setIsCheck([...isCheck, id])
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id))
    }
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),
    },
    {
      title: 'Name',
      accessor: 'firstName',
      render: (row) => (
        <span>
          {row.firstName} {row.lastName}
        </span>
      ),
    },
    {
      title: 'Phone',
      accessor: 'phone',
    },
    {
      title: 'Email',
      accessor: 'email',
    },
    {
      title: 'Last Activity',
      accessor: 'lastActivityDate',
      render: (row) => (
        <>
          {row.lastActivityDate !== null
            ? sanitizeDate(row.lastActivityDate)
            : '-'}
        </>
      ),
    },
    {
      title: 'View',
      accessor: '',
      render: (row) => (
        <button
          className="text-accent-1 hover:text-accent-1/90 underline inline-flex items-center gap-1"
          onClick={() => {
            navigate(`/settings/admins/${row.id}`)
          }}
        >
          Edit
        </button>
      ),
    },
  ]

  return (
    <>
      <Header
        pageTitle="Admins"
        breadcrumbs={['Global Settings', 'Admins']}
        actions={
          <>
            {hasEditPermission && (
              <Button
                variant="primary-bordered"
                onClick={() => {
                  setShowCreateModal(true)
                }}
              >
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title="Admins" icon="users-filled" className="mb-6" />
        <Table
          columns={columns}
          columnWidth="25%"
          isCheck={isCheck}
          data={admins}
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={pageChange}
      />
      <CreateAdminModal
        showCreateModal={showCreateModal}
        setShowCreateModal={setShowCreateModal}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
